import { useState, useCallback, useEffect } from 'react';
import { InterfaceNameValue } from '@tiendanube/components';
import { InterfaceNameBooleanValue } from 'commons/types';
import { useGetAddressesMexico } from 'domains/Billing/Invoices/pages/InvoicesInfoPage/components/Addresses';
import { PersonalDataState } from '../types';

interface UsePlaceOfResidenceProps {
  initialValues: PersonalDataState;
  handleOnChange: (
    data: InterfaceNameValue | InterfaceNameBooleanValue,
  ) => void;
  setMultipleFieldValues: (values: Record<string, any>) => void;
}

export function usePlaceOfResidence({
  initialValues,
  handleOnChange,
  setMultipleFieldValues,
}: UsePlaceOfResidenceProps) {
  const [isWithoutStreetNumberChecked, setIsWithoutStreetNumberChecked] =
    useState<boolean>(false);
  const [originalStreetNumber, setOriginalStreetNumber] = useState<number>(
    initialValues.streetNumber ?? 0,
  );
  const { fetchAddressesMexico, addressesMexico, isError, isLoading } =
    useGetAddressesMexico();

  useEffect(() => {
    if (!isError && !isLoading && addressesMexico) {
      const { province, city, locality } = addressesMexico;
      setMultipleFieldValues({
        state: province,
        city,
        locality,
      });
    }
  }, [addressesMexico, isError, isLoading, setMultipleFieldValues]);

  const clearFields = useCallback(() => {
    setMultipleFieldValues({
      state: '',
      city: '',
      locality: '',
    });
  }, [setMultipleFieldValues]);

  const handleZipCodeChange = useCallback(
    ({ value }: { value: string }): void => {
      if (value.length === 5) {
        fetchAddressesMexico(value);
      } else {
        clearFields();
      }
    },
    [fetchAddressesMexico, clearFields],
  );

  const handleChangeAddressesData = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
      property: keyof PersonalDataState,
    ) => {
      if (property === 'zipCode') {
        handleZipCodeChange({ value: e.target.value });
      }
      handleOnChange({ name: property, value: e.target.value });
    },
    [handleZipCodeChange, handleOnChange],
  );

  const handleChangeStreetNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, streetNumber: number) => {
      setIsWithoutStreetNumberChecked(e.target.checked);
      let value = 0;
      if (e.target.checked) {
        setOriginalStreetNumber(streetNumber);
      } else {
        value = originalStreetNumber;
      }
      handleOnChange({ name: 'streetNumber', value: String(value) });
    },
    [handleOnChange, originalStreetNumber],
  );

  return {
    isWithoutStreetNumberChecked,
    handleChangeAddressesData,
    handleChangeStreetNumber,
    isAddressesMexicoLoading: isLoading,
    isAddressesMexicoError: isError,
  };
}
